import { Pipe, PipeTransform } from '@angular/core';
import { LanguageOptions } from '@app/models';

interface NumberWordMap {
  units: Record<string, number>;
  teens: Record<string, number>;
  tens: Record<string, number>;
  hundreds: Record<string, number>;
  thousand: Record<string, number>;
  connector: string;
}

@Pipe({
  name: 'wordsToNumber',
  standalone: false
})
export class WordsToNumberPipe implements PipeTransform {
  private removeAccents(text: string): string {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  private readonly localeMaps: Record<LanguageOptions, NumberWordMap> = {
    'pt-BR': {
      units: {
        zero: 0,
        um: 1,
        uma: 1,
        dois: 2,
        duas: 2,
        tres: 3,
        quatro: 4,
        cinco: 5,
        seis: 6,
        sete: 7,
        oito: 8,
        nove: 9
      },
      teens: {
        dez: 10,
        onze: 11,
        doze: 12,
        treze: 13,
        quatorze: 14,
        catorze: 14,
        quinze: 15,
        dezesseis: 16,
        dezessete: 17,
        dezoito: 18,
        dezenove: 19
      },
      tens: {
        vinte: 20,
        trinta: 30,
        quarenta: 40,
        cinquenta: 50,
        sessenta: 60,
        setenta: 70,
        oitenta: 80,
        noventa: 90
      },
      hundreds: {
        cem: 100,
        cento: 100,
        duzentos: 200,
        trezentos: 300,
        quatrocentos: 400,
        quinhentos: 500,
        seiscentos: 600,
        setecentos: 700,
        oitocentos: 800,
        novecentos: 900
      },
      thousand: { mil: 1000 },
      connector: 'e'
    },
    'en-US': {
      units: {
        zero: 0,
        one: 1,
        two: 2,
        three: 3,
        four: 4,
        five: 5,
        six: 6,
        seven: 7,
        eight: 8,
        nine: 9
      },
      teens: {
        ten: 10,
        eleven: 11,
        twelve: 12,
        thirteen: 13,
        fourteen: 14,
        fifteen: 15,
        sixteen: 16,
        seventeen: 17,
        eighteen: 18,
        nineteen: 19
      },
      tens: {
        twenty: 20,
        thirty: 30,
        forty: 40,
        fifty: 50,
        sixty: 60,
        seventy: 70,
        eighty: 80,
        ninety: 90
      },
      hundreds: {
        hundred: 100,
        onehundred: 100,
        twohundred: 200,
        threehundred: 300,
        fourhundred: 400,
        fivehundred: 500,
        sixhundred: 600,
        sevenhundred: 700,
        eighthundred: 800,
        ninehundred: 900
      },
      thousand: { thousand: 1000 },
      connector: 'and'
    },
    'es-MX': {
      units: {
        zero: 0,
        cero: 0,
        un: 1,
        uno: 1,
        una: 1,
        dos: 2,
        tres: 3,
        cuatro: 4,
        cinco: 5,
        seis: 6,
        siete: 7,
        ocho: 8,
        nueve: 9
      },
      teens: {
        diez: 10,
        once: 11,
        doce: 12,
        trece: 13,
        catorce: 14,
        quince: 15,
        diecisiete: 17,
        dieciocho: 18,
        diecinueve: 19
      },
      tens: {
        veinte: 20,
        veintiuno: 21,
        veintiuna: 21,
        veintidos: 22,
        veintidós: 22,
        veintitres: 23,
        veintitrés: 23,
        veinticuatro: 24,
        veinticinco: 25,
        veintiseis: 26,
        veintiséis: 26,
        veintisiete: 27,
        veintiocho: 28,
        veintinueve: 29,
        treinta: 30,
        cuarenta: 40,
        cincuenta: 50,
        sesenta: 60,
        setenta: 70,
        ochenta: 80,
        noventa: 90
      },
      hundreds: {
        cien: 100,
        ciento: 100,
        doscientos: 200,
        doscientas: 200,
        trescientos: 300,
        trescientas: 300,
        cuatrocientos: 400,
        cuatrocientas: 400,
        quinientos: 500,
        quinientas: 500,
        seiscientos: 600,
        seiscientas: 600,
        setecientos: 700,
        setecientas: 700,
        ochocientos: 800,
        ochocientas: 800,
        novecientos: 900,
        novecientas: 900
      },
      thousand: { mil: 1000 },
      connector: 'y'
    }
  };

  transform(text: string, locale: LanguageOptions = 'en-US'): number | null {
    const map: NumberWordMap = this.localeMaps[locale];
    const normalizedText = this.removeAccents(text.toLowerCase());
    const words = normalizedText.split(/\s+/);

    let total = 0;
    let partial = 0;
    let found = false;

    for (const word of words) {
      if (map.units[word] !== undefined) {
        partial += map.units[word];
        found = true;
      } else if (map.teens[word] !== undefined) {
        partial += map.teens[word];
        found = true;
      } else if (map.tens[word] !== undefined) {
        partial += map.tens[word];
        found = true;
      } else if (map.hundreds[word] !== undefined) {
        partial += map.hundreds[word];
        found = true;
      } else if (map.thousand[word] !== undefined) {
        if (partial === 0) partial = 1;
        total += partial * 1000;
        partial = 0;
        found = true;
      } else if (word === map.connector) {
        continue;
      }
    }

    total += partial;
    return found ? total : null;
  }
}
